import React, { useEffect, useState } from 'react';
// import Select from "react-select";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n/i18n";
import { STRING_LANGUAGE_ULTIMA_MILLA } from '../../utils/const/string-const';

export default function LandingPage() {
    const history = useHistory();
    const { t } = useTranslation();
    const [activeFeature, setActiveFeature] = useState(0);
    const [selectedLanguage, setSelectedLanguage] = useState('es');
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

    const languages = [
        { value: 'en', label: 'English', flag: '🇺🇸' },
        { value: 'es', label: 'Español', flag: '🇪🇸' },
        { value: 'pt', label: 'Português', flag: '🇧🇷' }
    ];

    useEffect(() => {
        const language = localStorage.getItem(STRING_LANGUAGE_ULTIMA_MILLA);
        if (language) {
            i18n.changeLanguage(language);
        } else {
            i18n.changeLanguage('en');
        }
    }, []);

    const features = [
        {
            icon: "📍",
            title: t("label_seguimiento_en_tiempo_real"),
            description: t("label_tus_clientes_pueden_ver_exactamente_donde_esta_su_pedido_en_todo_momento"),
        },
        {
            icon: "📦",
            title: t("label_gestion_de_entregas"),
            description: t("label_carga_y_administra_todas_tus_entregas_desde_una_unica_plataforma"),
        },
        {
            icon: "🚚",
            title: t("label_optimizacion_de_rutas"),
            description: t("label_algoritmos_inteligentes_para_las_rutas_mas_eficientes"),
        },
        {
            icon: "🔔",
            title: t("label_notificaciones_automaticas"),
            description: t("label_alertas_instantaneas_para_clientes_y_gestores_de_flota"),
        },
        {
            icon: "📊",
            title: t("label_reportes_detallados"),
            description: t("label_analitica_completa_de_tiempos_eficiencia_y_satisfaccion"),
        },
        {
            icon: "⏱️",
            title: t("label_etas_precisos"),
            description: t("label_tiempos_estimados_de_llegada_actualizados_en_tiempo_real"),
        }
    ];

    const benefits = [
        {
            icon: "✅",
            title: t("label_mayor_satisfaccion_del_cliente"),
            description: t("label_transparencia_total_en_cada_entrega_genera_confianza_y_lealtad"),
        },
        {
            icon: "💰",
            title: t("label_reduccion_de_costos"),
            description: t("label_optimiza_rutas_y_reduce_llamadas_de_dnde_esta_mi_pedido"),
        },
        {
            icon: "📈",
            title: t("label_escalabilidad"),
            description: t("label_crece_sin_limites_desde_1_hasta_10000_entregas_diarias"),
        },
        {
            icon: "🎯",
            title: t("label_entregas_puntuales"),
            description: t("label_mejora_tus_tiempos_de_entrega_hasta_un_40"),
        }
    ];

    const scrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isLanguageDropdownOpen && !event.target.closest('.language-dropdown')) {
                setIsLanguageDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isLanguageDropdownOpen]);

    // Language Dropdown Component
    const LanguageDropdown = () => (
        <div className="relative language-dropdown">
            <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
            >
                <span className="text-xl">{languages.find(lang => lang.value === selectedLanguage)?.flag}</span>
                <span className="text-gray-700 text-sm font-medium">{languages.find(lang => lang.value === selectedLanguage)?.label}</span>
                <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    {languages.map((language) => (
                        <button
                            key={language.value}
                            onClick={() => {
                                setSelectedLanguage(language.value);
                                setIsLanguageDropdownOpen(false);
                                i18n.changeLanguage(language.value); // Explicitly change the language
                                localStorage.setItem(STRING_LANGUAGE_ULTIMA_MILLA, language.value);
                            }}
                            className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-blue-50 transition ${selectedLanguage === language.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                                }`}
                        >
                            <span className="text-xl">{language.flag}</span>
                            <span className="font-medium">{language.label}</span>
                            {selectedLanguage === language.value && (
                                <svg className="w-4 h-4 ml-auto text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const render = () => {
        try {
            const currentYear = new Date().getFullYear();
            return (
                <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
                    {/* Header/Navigation */}
                    <header className="bg-white shadow-md sticky top-0 z-50">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            {/* Main Header Row */}
                            <div className="flex justify-between items-center py-4">
                                <div className="flex items-center">
                                    <h1 className="text-2xl font-bold text-blue-600">DataTerra</h1>
                                    <span className="ml-2 text-sm text-gray-500">{t("label_tms")}</span>
                                </div>
                                <nav className="hidden md:flex space-x-8">
                                    <button onClick={() => scrollToSection('inicio')} className="text-gray-700 hover:text-blue-600 transition">{t("label_inicio")}</button>
                                    <button onClick={() => scrollToSection('caracteristicas')} className="text-gray-700 hover:text-blue-600 transition">{t("label_caracteristicas")}</button>
                                    <button onClick={() => scrollToSection('beneficios')} className="text-gray-700 hover:text-blue-600 transition">{t("label_beneficios")}</button>
                                    <button onClick={() => scrollToSection('contacto')} className="text-gray-700 hover:text-blue-600 transition">{t("label_contacto")}</button>
                                </nav>
                                <div className="flex items-center space-x-4">
                                    {/* Language Dropdown - Desktop Only */}
                                    <div className="hidden md:block">
                                        <LanguageDropdown />
                                    </div>

                                    <button
                                        onClick={() => history.push('/login')}
                                        className="text-blue-600 hover:text-blue-700 font-medium text-sm md:text-base"
                                    >
                                        {t("label_iniciar_sesion")}
                                    </button>
                                    {/* <button
                                        onClick={() => history.push('/login')}
                                        className="bg-blue-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-blue-700 transition shadow-md text-sm md:text-base"
                                    >
                                        {t("label_comenzar_ahora")}
                                    </button> */}
                                </div>
                            </div>

                            {/* Mobile Language Selector Row */}
                            <div className="md:hidden border-t border-gray-200 py-3 flex justify-center">
                                <LanguageDropdown />
                            </div>
                        </div>
                    </header>

                    {/* Hero Section */}
                    <section id="inicio" className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center">
                                <div className="inline-block mb-4">
                                    <span className="bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold">
                                        🚀 {t("label_tms_inteligente")}
                                    </span>
                                </div>
                                <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
                                    {t("label_transforma_tus_entregas_en")}
                                    <span className="text-blue-600"> {t("label_experiencias_inolvidables")}</span>
                                </h1>
                                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
                                    {t("label_la_plataforma_de_seguimiento_en_tiempo_real_que_conecta_tu_empresa_con_tus_clientes")}
                                    {t("label_ellos_ven_exactamente_donde_esta_su_pedido_tu_optimizas_cada_entrega")}
                                </p>

                                <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-12">
                                    {/* <button
                                        onClick={() => history.push('/login')}
                                        className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition shadow-lg transform hover:scale-105"
                                    >
                                        Prueba Gratis por 30 días
                                    </button> */}
                                    <button
                                        onClick={() => scrollToSection('caracteristicas')}
                                        className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition shadow-lg border-2 border-blue-600"
                                    >
                                        {t("label_ver_demo")}
                                    </button>
                                </div>

                                {/* Stats */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto mt-16">
                                    <div className="bg-white p-6 rounded-xl shadow-lg">
                                        <div className="text-4xl font-bold text-blue-600 mb-2">40%</div>
                                        <div className="text-gray-600">{t("label_mejora_en_tiempos_de_entrega")}</div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl shadow-lg">
                                        <div className="text-4xl font-bold text-blue-600 mb-2">95%</div>
                                        <div className="text-gray-600">{t("label_satisfaccion_del_cliente")}</div>
                                    </div>
                                    <div className="bg-white p-6 rounded-xl shadow-lg">
                                        <div className="text-4xl font-bold text-blue-600 mb-2">-60%</div>
                                        <div className="text-gray-600">{t("label_reduccion_de_llamadas")}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Decorative elements */}
                        {/* <div className="absolute top-0 right-0 -z-10 transform translate-x-1/2 -translate-y-1/2">
                            <div className="w-96 h-96 bg-blue-100 rounded-full opacity-20 blur-3xl"></div>
                        </div>
                        <div className="absolute bottom-0 left-0 -z-10 transform -translate-x-1/2 translate-y-1/2">
                            <div className="w-96 h-96 bg-purple-100 rounded-full opacity-20 blur-3xl"></div>
                        </div> */}
                    </section>

                    {/* Features Section */}
                    <section id="caracteristicas" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                    {t("label_todo_lo_que_necesitas_para_tms")}
                                </h2>
                                <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                                    {t("label_una_plataforma_completa_diseñada_para_empresas_que_entregan_productos_personalizados")}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {features.map((feature, index) => (
                                    <div
                                        key={index}
                                        className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer border border-gray-100"
                                        onMouseEnter={() => setActiveFeature(index)}
                                    >
                                        <div className="text-5xl mb-4">{feature.icon}</div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                        <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* How It Works */}
                    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                    {t("label_como_funciona")}
                                </h2>
                                <p className="text-xl text-gray-600">
                                    {t("label_tan_simple_que_empezaras_en_minutos")}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div className="relative">
                                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                                        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-6">
                                            1
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{t("label_carga_tus_entregas")}</h3>
                                        <p className="text-gray-600">
                                            {t("label_sube_tu_lista_de_pedidos_con_direcciones_de_clientes_puedes_hacerlo_manualmente_o_mediante_excel_api")}
                                        </p>
                                    </div>
                                    {/* Arrow for desktop */}
                                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-4xl text-blue-400">
                                        →
                                    </div>
                                </div>

                                <div className="relative">
                                    <div className="bg-white p-8 rounded-2xl shadow-lg">
                                        <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-6">
                                            2
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-4">{t("label_asigna_vehiculos")}</h3>
                                        <p className="text-gray-600">
                                            {t("label_el_sistema_optimiza_rutas_automaticamente_tus_conductores_reciben_las_entregas_en_su_app_movil")}
                                        </p>
                                    </div>
                                    <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-4xl text-blue-400">
                                        →
                                    </div>
                                </div>

                                <div className="bg-white p-8 rounded-2xl shadow-lg">
                                    <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-6">
                                        3
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-4">{t("label_clientes_rastrean")}</h3>
                                    <p className="text-gray-600">
                                        {t("label_tus_clientes_reciben_un_link_unico_para_ver_en_tiempo_real_donde_esta_su_pedido_sin_llamadas_ni_dudas")}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Benefits Section */}
                    <section id="beneficios" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                                    {t("label_por_que_elegirnos")}
                                </h2>
                                <p className="text-xl text-gray-600">
                                    {t("label_ventajas_que_transformaran_tu_operacion")}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-start space-x-4 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl hover:shadow-lg transition">
                                        <div className="text-4xl flex-shrink-0">{benefit.icon}</div>
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                                            <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* CTA Section */}
                    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-purple-600">
                        <div className="max-w-4xl mx-auto text-center">
                            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                {t("label_listo_para_revolucionar_tus_entregas")}
                            </h2>
                            <p className="text-xl text-blue-100 mb-8">
                                {t("label_unete_a_las_empresas_que_ya_estan_ofreciendo_la_mejor_experiencia_de_entrega")}
                            </p>
                            <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4">
                                {/* <button
                                    onClick={() => history.push('/login')}
                                    className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition shadow-xl transform hover:scale-105"
                                >
                                    Comienza tu prueba gratis
                                </button> */}
                                <button
                                    onClick={() => scrollToSection('contacto')}
                                    className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition"
                                >
                                    {t("label_hablar_con_ventas")}
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Footer */}
                    <footer id="contacto" className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                                <div>
                                    <h3 className="text-white text-lg font-bold mb-4">DataTerra</h3>
                                    {/* <p className="text-sm text-gray-400">
                                        Conectando empresas con clientes a través de la última milla inteligente.
                                    </p> */}
                                </div>
                                {/* <div>
                                    <h4 className="text-white font-semibold mb-4">Producto</h4>
                                    <ul className="space-y-2 text-sm">
                                        <li><button onClick={() => scrollToSection('caracteristicas')} className="hover:text-white transition">Características</button></li>
                                        <li><button onClick={() => scrollToSection('beneficios')} className="hover:text-white transition">Beneficios</button></li>
                                        <li><button className="hover:text-white transition">Precios</button></li>
                                        <li><button className="hover:text-white transition">Casos de éxito</button></li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="text-white font-semibold mb-4">Empresa</h4>
                                    <ul className="space-y-2 text-sm">
                                        <li><button className="hover:text-white transition">Nosotros</button></li>
                                        <li><button className="hover:text-white transition">Blog</button></li>
                                        <li><button className="hover:text-white transition">Contacto</button></li>
                                        <li><button className="hover:text-white transition">Soporte</button></li>
                                    </ul>
                                </div> */}
                                <div>
                                    <h4 className="text-white font-semibold mb-4">{t("label_contacto")}</h4>
                                    <ul className="space-y-2 text-sm">
                                        <li>📧 info@boltrack.com</li>
                                        <li>📞 +591 67701920</li>
                                        <li className="pt-4 flex space-x-4">
                                            <button className="hover:text-white transition">Facebook</button>
                                            <button className="hover:text-white transition">Twitter</button>
                                            <button className="hover:text-white transition">LinkedIn</button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center text-gray-500">
                                <p>© {currentYear} DataTerra. Todos los derechos reservados.</p>
                            </div>
                        </div>
                    </footer>
                </div>
            );
        } catch (err) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-red-50">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">Oops! Algo salió mal</h1>
                        <p className="text-gray-600">{err.message}</p>
                    </div>
                </div>
            );
        }
    }

    return render();
}